// src/private/src/pages/app/clients/ClientListPage.jsx
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Pagination,
} from '@mui/material';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import { LinkSimple } from '@phosphor-icons/react';
import api from '@src/api/clients';

const PAGE_SIZE = 25;
const CLIENTS_CACHE_TTL_MS = 5 * 60 * 1000;
const CLIENTS_LIST_CACHE = new Map();
const GOOGLE_ADS_STATIC_PARAMS =
  '&euid=391788443&__u=1069503107&uscid=7044144174&__c=3239473726&authuser=0&__e=2441890378';

const buildGoogleAdsLink = (customerId) =>
  `https://ads.google.com/aw/overview?ocid=${customerId}&ascid=${customerId}${GOOGLE_ADS_STATIC_PARAMS}`;

const buildFacebookAdsLibraryLink = (pageId) =>
  `https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=US&view_all_page_id=${pageId}`;

const FacebookBadgeIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" role="img" aria-label="Facebook" {...props}>
    <rect width="16" height="16" rx="4" fill="#1877F2" />
    <path
      d="M9.333 6H10.667V4.133H9.4C7.933 4.133 6.933 5.133 6.933 6.6V7.8H5.333V9.933H6.933V16H9.4V9.933H11L11.333 7.8H9.4V6.733C9.4 6.293 9.667 6 9.333 6Z"
      fill="#fff"
    />
  </svg>
);

const GoogleBadgeIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 18 18" role="img" aria-label="Google" {...props}>
    <rect x="1" y="1" width="16" height="16" rx="8" fill="#fff" stroke="#E0E0E0" strokeWidth="0.5" />
    <path
      fill="#4285F4"
      d="M16.318 8.205c0-.546-.049-1.07-.141-1.574H9v2.977h4.082c-.175.95-.702 1.758-1.494 2.295v1.908h2.414c1.414-1.302 2.316-3.219 2.316-5.606z"
    />
    <path
      fill="#34A853"
      d="M9 16.5c1.98 0 3.64-.655 4.853-1.776l-2.414-1.908c-.67.45-1.53.717-2.439.717-1.874 0-3.463-1.266-4.032-2.966H2.448v1.996A7.495 7.495 0 009 16.5z"
    />
    <path
      fill="#FBBC05"
      d="M4.968 10.567A4.495 4.495 0 014.73 9c0-.548.094-1.084.238-1.567V5.437H2.448A7.5 7.5 0 001.5 9c0 1.21.289 2.352.948 3.563l2.52-1.996z"
    />
    <path
      fill="#EA4335"
      d="M9 4.5c1.08 0 2.045.372 2.804 1.098l2.099-2.099C12.64 2.387 10.98 1.5 9 1.5a7.495 7.495 0 00-6.552 3.937l2.52 1.996C5.537 5.766 7.126 4.5 9 4.5z"
    />
  </svg>
);

const TikTokBadgeIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" role="img" aria-label="TikTok" {...props}>
    <rect width="16" height="16" rx="4" fill="#010101" />
    <path
      d="M7.2 13.6c-1.43 0-2.6-1.17-2.6-2.6 0-1.177.784-2.167 1.86-2.483v1.667a.95.95 0 00-.56.876c0 .526.43.956.96.956a1.01 1.01 0 00.7-.296V4.8h2.3c.026 1.232 1.018 2.224 2.25 2.25V8.8a3.47 3.47 0 01-2.25-.741v3.115c0 1.745-1.415 3.159-3.16 3.159Z"
      fill="#25F4EE"
    />
    <path
      d="M6.4 12.667c-1.01 0-1.833-.822-1.833-1.833 0-.42.146-.806.39-1.11V6.133H3.6v2.266c-.84.504-1.4 1.414-1.4 2.435 0 1.58 1.28 2.86 2.86 2.86 1.014 0 1.9-.517 2.433-1.302v-1.767c-.223.17-.5.272-.813.272-.69 0-1.25-.56-1.25-1.25 0-.07.006-.14.017-.207"
      fill="#FE2C55"
    />
    <path
      d="M10.6 5.2V4H8.3v7.633a1.02 1.02 0 01-.76.323c-.54 0-.98-.44-.98-.98 0-.54.44-.98.98-.98.34 0 .64.18.807.443V7.214c.62.43 1.377.686 2.173.686.2 0 .39-.017.58-.045V6.8a3.51 3.51 0 01-1.5-.934Z"
      fill="#fff"
    />
  </svg>
);

const PLATFORM_ICON_COMPONENTS = {
  facebook: FacebookBadgeIcon,
  google: GoogleBadgeIcon,
  tiktok: TikTokBadgeIcon,
};

const CLIENT_ACCOUNT_LINK_SX = {
  color: 'text.secondary',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  borderRadius: '50%',
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: (theme) => theme.palette.background.default,
  transition: 'all 0.15s ease',
  '&:hover': {
    textDecoration: 'none',
    transform: 'translateY(-1px)',
    boxShadow: (theme) => theme.shadows[1],
  },
};

const getClientAdAccountLinks = (client = {}) => {
  const facebookLinks = (Array.isArray(client.facebook_accounts) ? client.facebook_accounts : [])
    .filter((account) => account?.account_id)
    .map((account) => ({
      key: `fb-${account.account_id}`,
      href: `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${account.account_id}`,
      label: `Facebook: ${account.account_name || account.account_id}`,
      platform: 'facebook',
    }));

  const tiktokLinks = (Array.isArray(client.tiktok_advertisers) ? client.tiktok_advertisers : [])
    .filter((advertiser) => advertiser?.advertiser_id)
    .map((advertiser) => ({
      key: `tt-${advertiser.advertiser_id}`,
      href: `https://ads.tiktok.com/i18n/manage/campaign?aadvid=${advertiser.advertiser_id}`,
      label: `TikTok: ${advertiser.advertiser_name || advertiser.advertiser_id}`,
      platform: 'tiktok',
    }));

  const googleLinks = (Array.isArray(client.google_customers) ? client.google_customers : [])
    .filter((customer) => customer?.customer_id)
    .map((customer) => ({
      key: `gg-${customer.customer_id}`,
      href: buildGoogleAdsLink(customer.customer_id),
      label: `Google: ${customer.customer_name || customer.customer_id}`,
      platform: 'google',
    }));

  return [...facebookLinks, ...tiktokLinks, ...googleLinks];
};

const parseInitialStatus = (rawValue) => {
  if (rawValue === 'active' || rawValue === 'inactive' || rawValue === 'all') {
    return rawValue;
  }
  return 'active';
};

const parseInitialPage = (rawValue) => {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }
  return Math.floor(parsed);
};

const buildCacheKey = ({ page, debouncedSearch, statusFilter }) =>
  JSON.stringify({
    page,
    pageSize: PAGE_SIZE,
    q: debouncedSearch.trim(),
    status: statusFilter,
  });

const readClientsCache = (key) => {
  const cached = CLIENTS_LIST_CACHE.get(key);
  if (!cached) {
    return null;
  }
  if (Date.now() - cached.timestamp > CLIENTS_CACHE_TTL_MS) {
    CLIENTS_LIST_CACHE.delete(key);
    return null;
  }
  return cached.data;
};

const MAPPING_LINK_OPTIONS = [
  { type: 'email', label: 'Email' },
  { type: 'sftp', label: 'SFTP' },
  { type: 'api', label: 'API' },
  { type: 'google_sheet', label: 'GSheet' },
];

export default function ClientListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('q') || '';
  const initialStatus = parseInitialStatus(searchParams.get('status'));
  const initialPage = parseInitialPage(searchParams.get('page'));
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [page, setPage] = useState(initialPage);
  const [totalCount, setTotalCount] = useState(0);
  const initializedRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    const trimmedSearch = search.trim();
    if (trimmedSearch) {
      nextParams.set('q', trimmedSearch);
    }
    if (statusFilter !== 'active') {
      nextParams.set('status', statusFilter);
    }
    if (page > 1) {
      nextParams.set('page', String(page));
    }
    if (searchParams.toString() === nextParams.toString()) {
      return;
    }
    setSearchParams(nextParams, { replace: true });
  }, [page, search, statusFilter, searchParams, setSearchParams]);

  useEffect(() => {
    const cacheKey = buildCacheKey({ page, debouncedSearch, statusFilter });
    const cached = readClientsCache(cacheKey);
    if (cached) {
      setClients(cached.results);
      setTotalCount(cached.count);
      setLoading(false);
      return;
    }

    setLoading(true);
    const payload = {
      page,
      page_size: PAGE_SIZE,
      fields: [
        'id',
        'name',
        'slug',
        'client_logo_file',
        'client_logo_url',
        'active',
        'facebook_accounts',
        'facebook_pages',
        'tiktok_advertisers',
        'google_customers',
        'mapping_types',
      ].join(','),
    };
    const trimmedSearch = debouncedSearch.trim();
    if (trimmedSearch) {
      payload.name = trimmedSearch;
    }
    if (statusFilter === 'active') {
      payload.active = 'true';
    } else if (statusFilter === 'inactive') {
      payload.active = 'false';
    }

    api.request({
      url: 'dashboard/clients/',
      method: 'GET',
      payload,
    })
      .then((res) => {
        const data = res?.data;
        const results = Array.isArray(data) ? data : data?.results || [];
        const count = Array.isArray(data) ? data.length : data?.count ?? results.length;
        CLIENTS_LIST_CACHE.set(cacheKey, {
          timestamp: Date.now(),
          data: { results, count },
        });
        setClients(results);
        setTotalCount(count);
      })
      .catch((err) => console.error('Failed to load clients:', err))
      .finally(() => setLoading(false));
  }, [page, debouncedSearch, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const renderClientAccountLinks = (client) => {
    const links = getClientAdAccountLinks(client);
    if (links.length === 0) {
      return '-';
    }

    return (
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          flexWrap: 'wrap',
          rowGap: 0.5,
          maxWidth: 180,
          '& > *': { flexShrink: 0 },
        }}
      >
        {links.map((link, index) => {
          const IconComponent = PLATFORM_ICON_COMPONENTS[link.platform] || LinkSimple;
          return (
            <Tooltip key={`${link.key}-${index}`} title={link.label} arrow>
              <Typography
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={CLIENT_ACCOUNT_LINK_SX}
              >
                <IconComponent size={14} />
              </Typography>
            </Tooltip>
          );
        })}
      </Stack>
    );
  };

  const renderFacebookPageLinks = (client) => {
    const pages = Array.isArray(client?.facebook_pages) ? client.facebook_pages : [];
    const validPages = pages.filter((page) => page?.page_id && page?.page_name);
    if (validPages.length === 0) {
      return '-';
    }

    return (
      <Stack spacing={0.4} sx={{ maxWidth: 260 }}>
        {validPages.map((page) => (
          <Typography
            key={`page-${page.id || page.page_id || page.page_name}`}
            component="a"
            href={buildFacebookAdsLibraryLink(page.page_id)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'primary.main',
              textDecoration: 'none',
              fontSize: '0.82rem',
              lineHeight: 1.25,
              maxWidth: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            title={page.page_name}
          >
            <LinkSimple size={13} />
            {page.page_name}
          </Typography>
        ))}
      </Stack>
    );
  };

  const renderMappingLinks = (client) => {
    const availableTypes = new Set(
      Array.isArray(client?.mapping_types) ? client.mapping_types : []
    );
    const links = MAPPING_LINK_OPTIONS.filter((option) =>
      availableTypes.has(option.type)
    );

    if (links.length === 0) {
      return '-';
    }

    return (
      <Stack direction="row" spacing={0.8} flexWrap="wrap">
        {links.map((link) => (
          <Typography
            key={`${client.id}-${link.type}`}
            component={Link}
            to={`/email-mappings?client_id=${client.id}&category=${link.type}`}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontSize: '0.78rem',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {link.label}
          </Typography>
        ))}
      </Stack>
    );
  };

  const renderOfferLinks = (client) => (
    <Stack direction="row" spacing={0.8} flexWrap="wrap">
      <Typography
        component={Link}
        to={`/client-offers?client_id=${client.id}&offer_type=offer`}
        sx={{ color: 'primary.main', textDecoration: 'none', fontSize: '0.78rem', '&:hover': { textDecoration: 'underline' } }}
      >
        Ad
      </Typography>
      <Typography
        component={Link}
        to={`/client-offers?client_id=${client.id}&offer_type=lp_offer`}
        sx={{ color: 'primary.main', textDecoration: 'none', fontSize: '0.78rem', '&:hover': { textDecoration: 'underline' } }}
      >
        LP
      </Typography>
      <Typography
        component={Link}
        to={`/client-offers?client_id=${client.id}&offer_type=financing`}
        sx={{ color: 'primary.main', textDecoration: 'none', fontSize: '0.78rem', '&:hover': { textDecoration: 'underline' } }}
      >
        Financing
      </Typography>
    </Stack>
  );

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Clients</Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/clients/new')}
        >
          + Add Client
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Box display="flex" gap={2} mb={2}>
            <TextField
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" mt={5}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Dashboard</TableCell>
                  <TableCell>Ad Accounts</TableCell>
                  <TableCell>Facebook Pages</TableCell>
                  <TableCell>Offers</TableCell>
                  <TableCell>Landing Pages</TableCell>
                  <TableCell>Vertical ZIPs</TableCell>
                  <TableCell>Budgets</TableCell>
                  <TableCell>Mappings</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                {clients.map((client) => {
                  const clientSlug = client.slug;
                  return (
                  <TableRow key={client.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        {client.client_logo_file || client.client_logo_url ? (
                          <img
                            src={client.client_logo_file || client.client_logo_url}
                            alt="Logo"
                            style={{
                              height: 36,
                              width: 36,
                              objectFit: 'contain',
                              borderRadius: 6,
                              backgroundColor: '#fff',
                              padding: 4,
                              border: '1px solid #e0e0e0'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <Box
                            height={36}
                            width={36}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            borderRadius={6}
                            border="1px solid #e0e0e0"
                            bgcolor="#f5f5f5"
                            fontSize={12}
                            color="#999"
                          >
                            N/A
                          </Box>
                        )}
                        <Typography variant="body2" fontWeight={500}>
                          {client.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Link to={`/clients/${clientSlug}`}>Hub</Link>
                        <Link to={`/clients/${clientSlug}/dashboard`}>Dashboard</Link>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {renderClientAccountLinks(client)}
                    </TableCell>
                    <TableCell>
                      {renderFacebookPageLinks(client)}
                    </TableCell>
                    <TableCell>
                      {renderOfferLinks(client)}
                    </TableCell>
                    <TableCell>
                      <Typography
                        component={Link}
                        to={`/clients/client-landing-pages?client_id=${client.id}`}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontSize: '0.82rem',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Landing Pages
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        component={Link}
                        to={`/clients/client-zips?client_id=${client.id}`}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontSize: '0.82rem',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        ZIPs
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        component={Link}
                        to={`/client-budgets?client_id=${client.id}`}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontSize: '0.82rem',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Budgets
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {renderMappingLinks(client)}
                    </TableCell>
                    <TableCell>{client.active ? '✅' : '❌'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        component={Link}
                        to={`/clients/${clientSlug}?edit=1`}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!loading && (
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <Typography variant="body2" color="text.secondary">
                {totalCount} client{totalCount === 1 ? '' : 's'}
              </Typography>
              <Pagination
                page={page}
                count={totalPages}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
